import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { ForbiddenState } from '@/components/admin/states';
import {
  EVENT_OPS_FLOW_SECTIONS,
  OPS_PLACE_LABELS,
  VISIBILITY_GUIDE,
  type OpsPlace,
} from '@/features/help/eventOpsFlows';

const WHO_CLASS: Record<string, string> = {
  office: 'bg-sky-100 text-sky-800',
  student: 'bg-emerald-100 text-emerald-800',
  director: 'bg-amber-100 text-amber-900',
  success: 'bg-violet-100 text-violet-900',
  both: 'bg-wpcanvas text-wptext-strong',
};

const PLACE_CLASS: Record<OpsPlace, string> = {
  screen: 'bg-sky-50 text-sky-900 border-sky-200',
  real: 'bg-orange-50 text-orange-900 border-orange-200',
  both: 'bg-wpcanvas text-wptext-strong border-wpborder',
};

/**
 * 事務局専用のイベント種別業務フロー（D-282）。
 * 静的HTMLではログインなしで読めてしまうため、アプリ内＋事務局ロール限定にする。
 */
export function EventOpsFlowsPage(): ReactElement {
  const { user } = useAuth();

  if (user?.active_role !== 'office') {
    return (
      <>
        <PageHeader title="イベント種別ごとの業務フロー" />
        <ForbiddenState message="この資料は事務局のみ閲覧できます。" />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="イベント種別ごとの業務フロー"
        description="社外秘。順／誰が／いつ／どこで／対応／何をする／何のために／見える人・タイミング／自動メール。"
        actions={
          <Link to="/help" className="text-wpaccent hover:underline">
            ← マニュアル一覧
          </Link>
        }
      />

      <section className="mb-4 rounded-sm border border-wpnotice-warning bg-wpnotice-warning/10 px-4 py-3">
        <p className="font-semibold text-wptext-strong">取り扱い</p>
        <p className="text-wptext-strong">
          運営手順の内部資料です。受講生・理事・サクセス担当・社外には見せないでください。URLを共有しても、事務局以外は開けません。
        </p>
      </section>

      <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-4">
        <h2 className="mb-2 font-semibold text-wptext-strong">「対応」列の見方</h2>
        <ul className="flex flex-col gap-1 text-wptext-strong">
          <li>
            <PlaceBadge place="screen" /> 管理画面上での操作（クリック・入力・公開など）
          </li>
          <li>
            <PlaceBadge place="real" /> 対面・電話・お手元シート・Meet本体での発行など、画面の外
          </li>
          <li>
            <PlaceBadge place="both" /> リアルで行った結果を画面に反映する、またはその逆
          </li>
        </ul>
        <p className="mt-3 text-wptext-strong">
          会議URLは事務局のみ登録。Meetは rec@cxo-growth.jp で発行→録画予約したうえで、画面の概要にURLを入れます。
        </p>
        <p className="mt-2 text-wptext-strong">
          席次は「席次」タブで<strong>対象者CSVを出力 → お手元で table_no（卓番号 01〜20）を記入 →
          CSV取込 → 下書き版を確定・公開</strong>の順です。編成の判断はシステム外、一括登録は画面のCSVです。
        </p>
        <p className="mt-2 text-wptext-strong">
          「自動メール」列は種別プリセットの配信タイミングです。予約は<strong>イベント作成時</strong>に並び、公開前は送信時刻になってもスキップされます。文面・時刻の変更は業務マスターの通知テンプレ／プリセット、またはイベント詳細の「通知」タブです。
        </p>
      </section>

      <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-4">
        <h2 className="mb-2 font-semibold text-wptext-strong">
          いつ・誰が見られるか（アンケート／写真／会員情報など）
        </h2>
        <p className="mb-3 text-wptext-strong">
          下の表の「見える人・タイミング」列とあわせて読んでください。種別をまたぐ共通ルールです。
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] table-fixed border-collapse text-left text-sm text-wptext-strong">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[78%]" />
            </colgroup>
            <thead>
              <tr className="bg-wpcanvas">
                <th className="border border-wpborder px-3 py-2 font-semibold">対象</th>
                <th className="border border-wpborder px-3 py-2 font-semibold">見える人・タイミング</th>
              </tr>
            </thead>
            <tbody>
              {VISIBILITY_GUIDE.map((row) => (
                <tr key={row.topic}>
                  <td className="border border-wpborder px-3 py-2 align-top font-semibold">
                    {row.topic}
                  </td>
                  <td className="border border-wpborder px-3 py-2 align-top break-words">
                    {row.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <nav className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
        <p className="mb-2 font-semibold text-wptext-strong">目次</p>
        <ol className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {EVENT_OPS_FLOW_SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`} className="text-wpaccent hover:underline">
                {section.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="flex flex-col gap-6">
        {EVENT_OPS_FLOW_SECTIONS.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="rounded-sm border border-wpborder bg-white px-4 py-4"
          >
            <h2 className="mb-1 border-b-2 border-wpaccent pb-1 font-semibold text-wptext-strong">
              {section.title}
            </h2>
            <p className="mb-2 text-wptext-strong">{section.lead}</p>
            {section.note && (
              <p className="mb-3 rounded-sm border border-wpnotice-warning bg-wpnotice-warning/10 px-3 py-2 text-sm text-wptext-strong">
                {section.note}
              </p>
            )}
            {section.pending && (
              <p className="mb-3 text-sm text-wpnotice-warning">一部未確定（ヒアリング回答待ち）</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[96rem] table-fixed border-collapse text-left text-sm text-wptext-strong">
                <colgroup>
                  <col className="w-[3%]" />
                  <col className="w-[6%]" />
                  <col className="w-[7%]" />
                  <col className="w-[13%]" />
                  <col className="w-[4%]" />
                  <col className="w-[16%]" />
                  <col className="w-[12%]" />
                  <col className="w-[18%]" />
                  <col className="w-[21%]" />
                </colgroup>
                <thead>
                  <tr className="bg-wpcanvas">
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      順
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      誰が
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      いつ
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      どこで
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      対応
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      何をする
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      何のために
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      見える人・タイミング
                    </th>
                    <th className="border border-wpborder px-3 py-2 font-semibold text-wptext-strong">
                      自動メール
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.steps.map((step) => (
                    <tr key={`${section.id}-${step.seq}`}>
                      <td className="border border-wpborder px-3 py-2 text-center align-top font-semibold text-wptext-strong">
                        {step.seq}
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top">
                        <span
                          className={`inline-block rounded-sm px-1.5 py-0.5 text-xs font-semibold ${WHO_CLASS[step.who]}`}
                        >
                          {step.whoLabel}
                        </span>
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top text-wptext-strong">
                        {step.when}
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top break-words">
                        <code className="block whitespace-pre-wrap break-words rounded-sm border border-wpborder bg-wpcanvas px-1 text-xs text-wptext-strong">
                          {step.where}
                        </code>
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top">
                        <PlaceBadge place={step.place} />
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top break-words text-wptext-strong">
                        {step.what}
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top break-words text-wptext-strong">
                        {step.why}
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top break-words text-wptext-strong">
                        {step.visibility ?? '—'}
                      </td>
                      <td className="border border-wpborder px-3 py-2 align-top break-words text-wptext-strong">
                        {step.auto ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function PlaceBadge({ place }: { place: OpsPlace }): ReactElement {
  return (
    <span
      className={`inline-block rounded-sm border px-1.5 py-0.5 text-xs font-semibold ${PLACE_CLASS[place]}`}
    >
      {OPS_PLACE_LABELS[place]}
    </span>
  );
}
